/**
 * Error Types for Studio1
 * Defines reportable error structure for copyable/reportable error messages
 */

import { extractErrorTk, extractErrorParams } from "@/utils/errorUtils";

/**
 * ReportableError represents an error that can be copied and reported via the support form.
 * Includes all relevant context IDs for debugging.
 */
export interface ReportableError {
  /** Human-readable error message */
  message: string;
  /** Additional description or details */
  description?: string;
  /** i18n translation key (e.g., "errors.provider.kling.promptEmpty") */
  tk?: string;
  /**
   * Interpolation params for `tk`, supplied by the backend alongside the key
   * (e.g. a spend-denied billing error carries the blocking reason's params
   * `{ frozenCredits, outstandingCredits }`). Passed to i18next so the message
   * reads with the real figures. Numeric values are locale-formatted at render.
   */
  params?: Record<string, unknown>;
  /** UUIDv7 correlation ID tracking request through all layers */
  correlationId?: string;
  /** UUIDv7 event ID for the specific request */
  eventId?: string;
  /** Error code (e.g., "VALIDATION_ERROR", "NOT_FOUND") */
  code?: string;
  /** ISO 8601 timestamp when error occurred */
  timestamp: string;

  // Context IDs for debugging
  /** Workflow ID if error occurred in workflow context */
  workflowId?: string;
  /** Execution ID if error occurred during workflow execution */
  executionId?: string;
  /** Node ID if error occurred in a specific node */
  nodeId?: string;
  /** Project ID if error occurred in project context */
  projectId?: string;
  /** Timeline ID if error occurred in timeline context */
  timelineId?: string;
}

/**
 * API error structure returned from backend
 */
export interface ApiErrorResponse {
  code?: string;
  message?: string;
  tk?: string;
  details?: unknown;
}

/**
 * Extract a string field from a deeply nested unknown object.
 * Tries each accessor in order, returns the first string found.
 */
function extractStringField(input: unknown, field: string): string | undefined {
  if (typeof input !== "object" || input === null) return undefined;

  const obj = input as Record<string, unknown>;

  // 1. input.error.<field>
  const asErrorNested = obj["error"] as Record<string, unknown> | undefined;
  if (typeof asErrorNested?.[field] === "string")
    return asErrorNested[field] as string;

  // 2. input.<field> (top-level of response envelope)
  if (typeof obj[field] === "string") return obj[field] as string;

  // 3. input.response.data.error.<field> (axios-style)
  const resp = obj["response"] as Record<string, unknown> | undefined;
  const respData = resp?.["data"] as Record<string, unknown> | undefined;
  const respError = respData?.["error"] as Record<string, unknown> | undefined;
  if (typeof respError?.[field] === "string") return respError[field] as string;
  // Also try input.response.data.<field>
  if (typeof respData?.[field] === "string") return respData[field] as string;

  // 4. input.data.error.<field> (openapi-ts shape)
  const data = obj["data"] as Record<string, unknown> | undefined;
  const dataError = data?.["error"] as Record<string, unknown> | undefined;
  if (typeof dataError?.[field] === "string") return dataError[field] as string;
  // Also try input.data.<field>
  if (typeof data?.[field] === "string") return data[field] as string;

  // 5. input.body.error.<field>
  const body = obj["body"] as Record<string, unknown> | undefined;
  const bodyError = body?.["error"] as Record<string, unknown> | undefined;
  if (typeof bodyError?.[field] === "string") return bodyError[field] as string;
  // Also try input.body.<field>
  if (typeof body?.[field] === "string") return body[field] as string;

  return undefined;
}

/**
 * Extracts a ReportableError from various error types
 */
export function extractReportableError(
  error: unknown,
  context?: Partial<
    Pick<
      ReportableError,
      "workflowId" | "executionId" | "nodeId" | "projectId" | "timelineId"
    >
  >,
): ReportableError {
  const timestamp = new Date().toISOString();

  // Handle plain Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      timestamp,
      ...context,
    };
  }

  // Handle API error responses (object with message)
  if (typeof error === "object" && error !== null) {
    const obj = error as Record<string, unknown>;

    // Extract message from various shapes
    const message =
      extractStringField(error, "message") ||
      (typeof obj["message"] === "string" ? obj["message"] : undefined) ||
      "Unknown error occurred";

    const description =
      typeof obj["description"] === "string" ? obj["description"] : undefined;

    return {
      message,
      description,
      tk: extractErrorTk(error),
      params: extractErrorParams(error),
      correlationId: extractStringField(error, "correlationId"),
      eventId: extractStringField(error, "eventId"),
      code: extractStringField(error, "code"),
      timestamp,
      ...context,
    };
  }

  // Handle string errors
  if (typeof error === "string") {
    return {
      message: error,
      timestamp,
      ...context,
    };
  }

  // Fallback for unknown error types
  return {
    message: "Unknown error occurred",
    timestamp,
    ...context,
  };
}

/**
 * Formats a ReportableError for display in clipboard
 */
export function formatErrorForClipboard(error: ReportableError): string {
  const lines: string[] = [];

  lines.push("[Error Report]");
  lines.push(`Message: ${error.message}`);

  if (error.description) {
    lines.push(`Description: ${error.description}`);
  }

  if (error.code) {
    lines.push(`Code: ${error.code}`);
  }

  if (error.tk) {
    lines.push(`Tk: ${error.tk}`);
  }

  if (error.correlationId) {
    lines.push(`Correlation ID: ${error.correlationId}`);
  }

  if (error.eventId) {
    lines.push(`Event ID: ${error.eventId}`);
  }

  lines.push(`Timestamp: ${error.timestamp}`);

  // Add context IDs if present
  const contextIds: string[] = [];
  if (error.workflowId) contextIds.push(`Workflow: ${error.workflowId}`);
  if (error.executionId) contextIds.push(`Execution: ${error.executionId}`);
  if (error.nodeId) contextIds.push(`Node: ${error.nodeId}`);
  if (error.projectId) contextIds.push(`Project: ${error.projectId}`);
  if (error.timelineId) contextIds.push(`Timeline: ${error.timelineId}`);

  if (contextIds.length > 0) {
    lines.push("");
    lines.push("[Context]");
    lines.push(...contextIds);
  }

  return lines.join("\n");
}

/**
 * Formats a ReportableError for prefilling support form message
 */
export function formatErrorForSupportForm(error: ReportableError): string {
  const lines: string[] = [];

  lines.push("[Error Report]");
  lines.push(`Message: ${error.message}`);

  if (error.code) {
    lines.push(`Code: ${error.code}`);
  }

  if (error.tk) {
    lines.push(`Tk: ${error.tk}`);
  }

  lines.push(`Timestamp: ${error.timestamp}`);

  lines.push("");
  lines.push("--- Additional Context ---");
  lines.push("(Please describe what you were doing when this error occurred)");
  lines.push("");

  return lines.join("\n");
}
